import type { QueryClient } from "@tanstack/react-query";

import type { PrefetchedContact } from "@/lib/actions/resolution/types";

type CachedContact = {
  id: string;
  email?: string | null;
  name?: string | null;
};

function toPrefetchedContacts(contacts: CachedContact[]): PrefetchedContact[] {
  return contacts
    .filter(
      (contact): contact is CachedContact & { email: string } =>
        typeof contact.email === "string" && contact.email.trim().length > 0,
    )
    .map((contact) => ({
      id: contact.id,
      email: contact.email.trim(),
      name: contact.name?.trim() || undefined,
    }));
}

/**
 * Read company contacts from TanStack Query cache (e.g. after company detail load).
 * Skips limited queries — partial lists are not safe for auto-select-all send email.
 */
export function readCompanyContactsFromCache(
  queryClient: QueryClient,
  companyId: string,
): PrefetchedContact[] | undefined {
  const entries = queryClient.getQueriesData<CachedContact[]>({
    queryKey: ["company-contacts", companyId],
  });

  for (const [queryKey, data] of entries) {
    const params = queryKey[2] as { limit?: number } | undefined;
    if (params?.limit) {
      continue;
    }
    if (!Array.isArray(data) || data.length === 0) {
      continue;
    }

    const prefetched = toPrefetchedContacts(data);
    if (prefetched.length > 0) {
      return prefetched;
    }
  }

  return undefined;
}
